import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { useToast } from '@/hooks/use-toast';
import { useAuth, useRedirectIfAuthenticated } from '@/contexts/AuthContext';
import { SEO } from '@/components/seo';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { GoogleAuthButton, AuthDivider } from '@/components/auth/GoogleAuthButton';

const Login = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { language, isRTL, t } = useLanguage();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useRedirectIfAuthenticated();

  const from = (location.state as { from?: Location })?.from?.pathname;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        toast({
          title: t('auth.login.success.title'),
          description: t('auth.login.success.desc'),
        });
        if (from) {
          navigate(from, { replace: true });
        }
      } else {
        toast({
          title: t('auth.login.error.title'),
          description: result.message || t('auth.login.error.credentials'),
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t('auth.login.error.title'),
        description: error instanceof Error ? error.message : t('auth.login.error.generic'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Only disable while THIS form is submitting. We deliberately do NOT
  // disable on `isLoading` from AuthContext because that flag flickers
  // true→false during the post-logout "check session" round-trip,
  // briefly disabling the form right after the user lands on /login.
  const isFormDisabled = isSubmitting;

  return (
    <Layout>
      <SEO titleKey="seo.login.title" descriptionKey="seo.login.description" keywordsKey="seo.login.keywords" noindex={true} />

      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center bg-background py-12" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Background pattern */}
        <div className="absolute inset-0 islamic-pattern-overlay opacity-30 pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 w-full max-w-md mx-auto px-4"
        >
          <Card className="border-border shadow-soft-lg">
            <CardHeader className="text-center pb-2">
              {/* Logo */}
              <div className="mx-auto mb-4 w-14 h-14 rounded-xl bg-primary flex items-center justify-center shadow-glow-emerald">
                <span className="text-primary-foreground font-arabic text-2xl font-bold leading-none">هـ</span>
              </div>
              <CardTitle className="text-2xl font-bold text-foreground">
                {t('auth.login.heading')}
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                {t('auth.login.subheading')}
              </CardDescription>
            </CardHeader>

            <CardContent className="pt-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground">{t('email')}</Label>
                  <div className="relative">
                    <Mail className={`absolute ${isRTL ? 'end-3' : 'start-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('auth.login.email.placeholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`${isRTL ? 'pe-10' : 'ps-10'} rounded-lg`}
                      required
                      disabled={isFormDisabled}
                      autoComplete="email"
                      dir="ltr"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-foreground">{t('auth.login.password.label')}</Label>
                    <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                      {t('auth.login.forgotPassword')}
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className={`absolute ${isRTL ? 'end-3' : 'start-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('auth.login.password.placeholder')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`${isRTL ? 'pe-10 ps-10' : 'ps-10 pe-10'} rounded-lg`}
                      required
                      disabled={isFormDisabled}
                      autoComplete="current-password"
                      dir="ltr"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute ${isRTL ? 'start-3' : 'end-3'} top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors`}
                      aria-label={showPassword ? t('auth.login.password.hide') : t('auth.login.password.show')}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                    </button>
                  </div>
                </div>

                <Button type="submit" className="w-full rounded-lg h-11 font-semibold" disabled={isFormDisabled}>
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('auth.login.signingIn')}
                    </span>
                  ) : (
                    t('auth.login.submit')
                  )}
                </Button>
              </form>

              <div className="mt-4 space-y-4">
                <AuthDivider label={t('auth.google.or')} />
                <GoogleAuthButton label={t('auth.google.continue')} disabled={isFormDisabled} />
              </div>
            </CardContent>

            <CardFooter className="flex justify-center pb-6">
              <p className="text-sm text-muted-foreground">
                {t('auth.login.noAccount')}
                <Link to="/register" className="text-primary hover:underline font-medium">
                  {t('auth.login.register')}
                </Link>
              </p>
            </CardFooter>
          </Card>
        </motion.div>
      </div>
    </Layout>
  );
};

export default Login;
